<template>
  <modal id="questions-conditions-modal" ref="conditionsModal" @close="closeConditionModal" size="lg">
    <template slot="title">
      {{ t('forms-conditions-modal_title') }}
    </template>
    <template slot="subtitle">
      <!-- i18n-validator-ignore-next-line: runtime question text -->
      {{ questionLabel }}
    </template>
    <template slot="body">
      <div class="conditions-container">
        <p class="conditions-hint">
          {{ t('forms-conditions-header_text') }}
        </p>

        <div v-if="availableQuestions.length === 0" class="conditions-warning">
          <Icon icon-class="ui-alert-triangle" size="3" />
          <span>{{ t('forms-conditions-no_previous_warning') }}</span>
        </div>

        <condition-group
          v-else
          :group="conditionGroup"
          :level="0"
          :available-questions="availableQuestions"
          @change="handleConditionChange"
        />
      </div>
    </template>
    <template slot="footer">
      <kv-button variant="outlined" @click="closeConditionModal()">
        {{ t('forms-conditions-btn_cancel') }}
      </kv-button>
      <kv-button
        variant="filled"
        :disabled="availableQuestions.length === 0"
        @click="saveConditions()"
      >
        {{ t('forms-conditions-btn_confirm') }}
      </kv-button>
    </template>
  </modal>
</template>

<script>
import ConditionGroup from './condition-group.vue';
import { htmlToText } from "../../helpers/html";
import Icon from "../../components/icon/icon";
import KvButton from "../../components/form/kv-button.vue";

export default {
  name: "QuestionsConditions",
  components: {
    ConditionGroup,
    Icon,
    "kv-button": KvButton,
  },
  props: {
    value: {
      type: Object,
      required: true
    },
    questions: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      conditionGroup: {
        conditional_operator: "OR",
        childrens: [],
        conditions: []
      }
    };
  },
  computed: {
    // Content blocks hold rich text, so the subtitle needs its plain text.
    questionLabel() {
      return htmlToText(this.value.question).replace(/\s+/g, " ").trim();
    },
    availableQuestions() {
      const currentIndex = this.questions.findIndex(q => q.id === this.value.id);
      // File and table answers have no single comparable value and content
      // blocks collect no answer at all, so none of them can drive a condition.
      return this.questions.filter((q, index) =>
        q.id !== this.value.id &&
        !q.removed &&
        q.type !== 'file' &&
        q.type !== 'content' &&
        q.type !== 'table' &&
        index < currentIndex
      );
    }
  },
  mounted() {
    this.loadConditions();
  },
  watch: {
    value: {
      handler() {
        this.loadConditions();
      },
      immediate: false
    }
  },
  methods: {
    t(term, replaces) {
      return this.$root.t(term, replaces);
    },
    loadConditions() {
      if (this.value.visibility_condition && Object.keys(this.value.visibility_condition).length > 0) {
        this.conditionGroup = JSON.parse(JSON.stringify(this.value.visibility_condition));
      } else {
        this.conditionGroup = {
          conditional_operator: "OR",
          childrens: [],
          conditions: []
        };
      }
    },
    openConditionalModal() {
      this.$refs.conditionsModal.open();
    },
    closeConditionModal() {
      this.$refs.conditionsModal.close();
    },
    handleConditionChange(updatedGroup) {
      this.conditionGroup = updatedGroup;
    },
    validateCondition(value) {
      const hasValue = value.condition_value !== "" && value.condition_value !== null && value.condition_value !== undefined;
      return value.question_id && value.comparison_operator && (
        value.comparison_operator === "QUESTION_ANSWERED" || hasValue
      );
    },
    validateConditionsObject(conditions) {
        for (const condition of conditions) {
          if (!this.validateCondition(condition)) {
            this.$store.dispatch(
                "notification/error",
                this.t('forms-conditions-error_fill_all'),
            );
            return false;
          }
        }
        return true;
    },
    saveConditions() {
      const conditionsObj = JSON.parse(JSON.stringify(this.conditionGroup));
      if (conditionsObj.conditions.length > 0) {
        if (!this.validateConditionsObject(conditionsObj.conditions)) {
          return;
        }
      }
      if (conditionsObj.childrens.length > 0) {
        for (const childGroup of conditionsObj.childrens) {
          if (childGroup.conditions.length > 0) {
            if (!this.validateConditionsObject(childGroup.conditions)) {
              return;
            }
          }
        }
      }
      this.$emit('update:conditions', conditionsObj);
      this.closeConditionModal();
    }
  }
};
</script>

<style lang="scss" scoped>
.conditions-container {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.conditions-hint {
  margin: 0;
  font-size: 13px;
  line-height: 1.5;
  color: #6c737f;
}

.conditions-warning {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  padding: 10px 12px;
  border-radius: 8px;
  background: #fffaeb;
  color: #b54708;
  font-size: 13px;
}
</style>
